import './App.css';
import { useEffect, useState, useCallback } from "react";

function Resa({ token }) {
  // --- States ---
  const [reservations, setReservations] = useState([]);
  const [businessExpenses, setBusinessExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [reservationForm, setReservationForm] = useState({
    name: "", start_date: "", end_date: "", platform: "", income: "", payment_date: "", is_payed: false, comment: "", phone_number: "", country: "", _id: null
  });

  const [businessExpenseForm, setBusinessExpenseForm] = useState({
    name: "", value: "", date: "", comment: "", is_payed: false, provider: "", category: "", _id: null
  });

  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showBusinessExpenseModal, setShowBusinessExpenseModal] = useState(false);



  const API_URL = process.env.REACT_APP_API_URL; // change selon ton backend

  // --- Fetch Reservations ---
  const fetchReservations = useCallback(async () => {
    const res = await fetch(`${API_URL}/reservation`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = await res.json();
    // tri décroissant par date
    const sortedData = data.sort((a, b) => new Date(b.start_date) - new Date(a.end_date));
    setReservations(sortedData);
  }, [token, API_URL]);

  // --- Fetch Business Expenses ---
  const fetchBusinessExpenses = useCallback(async () => {
    const res = await fetch(`${API_URL}/business_expense`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = await res.json();
    // tri décroissant par date
    const sortedData = data.sort((a, b) => new Date(b.date) - new Date(a.date));
    setBusinessExpenses(sortedData);
  }, [token, API_URL]);

  const filteredReservations = reservations.filter(r => {
    if (!startDate && !endDate) return true;
    const date = new Date(r.start_date);
    if (startDate) {
      const [y, m, d] = startDate.split('-').map(Number);
      const start = new Date(y, m - 1, d); // Local midnight
      if (date < start) return false;
    }
    if (endDate) {
      const [y, m, d] = endDate.split('-').map(Number);
      const end = new Date(y, m - 1, d, 23, 59, 59, 999); // Local end of day
      if (date > end) return false;
    }
    return true;
  });

  const filteredBusinessExpenses = businessExpenses.filter(b => {
    if (!startDate && !endDate) return true;
    const date = new Date(b.date);
    if (startDate) {
      const [y, m, d] = startDate.split('-').map(Number);
      const start = new Date(y, m - 1, d);
      if (date < start) return false;
    }
    if (endDate) {
      const [y, m, d] = endDate.split('-').map(Number);
      const end = new Date(y, m - 1, d, 23, 59, 59, 999);
      if (date > end) return false;
    }
    return true;
  });

  // --- Totaux ---
  const totalReservations = filteredReservations.reduce((sum, r) => sum + (Number(r.income) || 0), 0);
  const totalBusinessExpenses = filteredBusinessExpenses.reduce((sum, e) => sum + (Number(e.value) || 0), 0);
  const netTotal = totalReservations - totalBusinessExpenses;


  // --- Load Data ---
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // Set default start date to first day of current month
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const localDate = new Date(firstDay.getTime() - firstDay.getTimezoneOffset() * 60000);
      setStartDate(localDate.toISOString().split("T")[0]);
      
      await fetchReservations();
      await fetchBusinessExpenses();
      setLoading(false);
    };
    load();
  }, [fetchReservations, fetchBusinessExpenses]);

  // --- Save Reservation ---
  const saveReservation = async (e) => {
    e.preventDefault();
    if (reservationForm._id) {
      await fetch(`${API_URL}/reservation/${reservationForm._id}`, {
        method: "PUT",
        headers: {
          'Authorization': `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(reservationForm),
      });
    } else {
      await fetch(`${API_URL}/reservation`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(reservationForm),
      });
    }
    setReservationForm({ name: "", start_date: "", end_date: "", platform: "", income: "", payment_date: "", is_payed: false, comment: "", _id: null });
    fetchReservations();
  };

  const deleteReservation = async (id) => {
    await fetch(`${API_URL}/reservation/${id}`, {
      method: "DELETE",
      headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchReservations();
  };

  // --- Save Business Expense ---
  const saveBusinessExpense = async (e) => {
    e.preventDefault();
    if (businessExpenseForm._id) {
      await fetch(`${API_URL}/business_expense/${businessExpenseForm._id}`, {
        method: "PUT",
        headers: {
          'Authorization': `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(businessExpenseForm),
      });
    } else {
      await fetch(`${API_URL}/business_expense`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(businessExpenseForm),
      });
    }
    setBusinessExpenseForm({ name: "", value: "", date: "", comment: "", is_payed: false, provider: "", category: "", _id: null });
    fetchBusinessExpenses();
  };

  const deleteBusinessExpense = async (id) => {
    await fetch(`${API_URL}/business_expense/${id}`, {
      method: "DELETE",
      headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchBusinessExpenses();
  };

  const formatDateInput = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().split("T")[0];
  };

  const calculateNights = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) return <div className="p-4">Loading...</div>;

  return (
    <div className="p-6 bg-gray-100 min-h-screen space-y-10">
      {/* --- le filtre sur les dates  --- */}
      <div className="bg-white p-4 rounded shadow mb-6">
        <h2 className="text-lg font-bold mb-2">Filtrer par dates</h2>
        <div className="flex gap-4">
          <div>
            <label className="block text-sm font-medium">Date de début</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border p-1 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Date de fin</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border p-1 rounded"
            />
          </div>
        </div>
      </div>
      {/* --- les totaux  --- */}
      <div className="bg-white p-6 rounded shadow mb-6 flex justify-around text-center">
        <div>
          <h3 className="text-lg font-bold text-green-600">Total Reservations</h3>
          <p className="text-xl">{totalReservations.toFixed(2)} MAD</p>
        </div>
        <div>
          <h3 className="text-lg font-bold text-red-600">Total Business Expenses</h3>
          <p className="text-xl">{totalBusinessExpenses.toFixed(2)} MAD</p>
        </div>
        <div>
          <h3 className="text-lg font-bold text-blue-600">Net</h3>
          <p className="text-xl">{netTotal.toFixed(2)} MAD</p>
        </div>
      </div>

      {/* --- Reservations Table --- */}
      <div className="bg-white p-4 rounded shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Reservations</h2>
          <button onClick={() => { setReservationForm({ name: "", start_date: "", end_date: "", platform: "", income: "", payment_date: "", is_payed: false, comment: "", phone_number: "", country: "", _id: null }); setShowReservationModal(true); }} className="bg-green-500 text-white px-3 py-1 rounded">+ Ajouter Réservation</button>
        </div>
        <table className="w-full table-auto border-collapse border">
          <thead>
            <tr className="bg-gray-200">
              <th className="border px-2 py-1">Name</th>
              <th className="border px-2 py-1">Phone</th>
              <th className="border px-2 py-1">Country</th>
              <th className="border px-2 py-1">Platform</th>
              <th className="border px-2 py-1">Income</th>
              <th className="border px-2 py-1">Cost/Night</th>
              <th className="border px-2 py-1">Nights</th>
              <th className="border px-2 py-1">Start</th>
              <th className="border px-2 py-1">End</th>
              <th className="border px-2 py-1">Payment Date</th>
              <th className="border px-2 py-1">Payed?</th>
              <th className="border px-2 py-1">Comment</th>
              <th className="border px-2 py-1">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReservations.map(res => {
              const nights = calculateNights(res.start_date, res.end_date);
              const costPerNight = nights > 0 ? (res.income / nights).toFixed(2) : "0.00";
              return (
                <tr key={res._id}>
                  <td className="border px-2 py-1">{res.name}</td>
                  <td className="border px-2 py-1">{res.phone_number}</td>
                  <td className="border px-2 py-1">{res.country}</td>
                  <td className="border px-2 py-1">{res.platform}</td>
                  <td className="border px-2 py-1">{res.income}</td>
                  <td className="border px-2 py-1">{costPerNight}</td>
                  <td className="border px-2 py-1">{nights}</td>
                  <td className="border px-2 py-1">{new Date(res.start_date).toLocaleDateString()}</td>
                  <td className="border px-2 py-1">{new Date(res.end_date).toLocaleDateString()}</td>
                  <td className="border px-2 py-1">{res.payment_date ? new Date(res.payment_date).toLocaleDateString() : ""}</td>
                  <td className="border px-2 py-1">{res.is_payed ? "Yes" : "No"}</td>
                  <td className="border px-2 py-1">{res.comment}</td>
                  <td className="border px-2 py-1 space-x-1">
                    <button onClick={() => { setReservationForm(res); setShowReservationModal(true); }} className="bg-yellow-400 px-2 py-1 rounded">Edit</button>
                    <button onClick={() => deleteReservation(res._id)} className="bg-red-500 text-white px-2 py-1 rounded">Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Reservation Modal */}
      {showReservationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-96 max-h-96 overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{reservationForm._id ? "Modifier Réservation" : "Ajouter Réservation"}</h2>
            <form onSubmit={(e) => { saveReservation(e); setShowReservationModal(false); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium">Name</label>
                <input type="text" className="w-full border p-2 rounded" value={reservationForm.name} onChange={e => setReservationForm({ ...reservationForm, name: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium">Phone Number</label>
                <input type="text" className="w-full border p-2 rounded" value={reservationForm.phone_number} onChange={e => setReservationForm({ ...reservationForm, phone_number: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium">Country</label>
                <input type="text" className="w-full border p-2 rounded" value={reservationForm.country} onChange={e => setReservationForm({ ...reservationForm, country: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium">Date Début</label>
                <input type="date" className="w-full border p-2 rounded" value={formatDateInput(reservationForm.start_date)} onChange={e => setReservationForm({ ...reservationForm, start_date: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium">Date Fin</label>
                <input type="date" className="w-full border p-2 rounded" value={formatDateInput(reservationForm.end_date)} onChange={e => setReservationForm({ ...reservationForm, end_date: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium">Platform</label>
                <input type="text" className="w-full border p-2 rounded" value={reservationForm.platform} onChange={e => setReservationForm({ ...reservationForm, platform: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium">Income</label>
                <input type="number" className="w-full border p-2 rounded" value={reservationForm.income} onChange={e => setReservationForm({ ...reservationForm, income: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium">Date Paiement</label>
                <input type="date" className="w-full border p-2 rounded" value={formatDateInput(reservationForm.payment_date)} onChange={e => setReservationForm({ ...reservationForm, payment_date: e.target.value })} />
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={reservationForm.is_payed} onChange={e => setReservationForm({ ...reservationForm, is_payed: e.target.checked })} />
                  <span className="text-sm font-medium">Is Payed</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium">Comment</label>
                <input type="text" className="w-full border p-2 rounded" value={reservationForm.comment} onChange={e => setReservationForm({ ...reservationForm, comment: e.target.value })} />
              </div>
              <div className="flex gap-2 pt-4">
                <button type="button" onClick={() => setShowReservationModal(false)} className="flex-1 bg-gray-400 text-white px-3 py-2 rounded">Annuler</button>
                <button type="submit" className="flex-1 bg-blue-500 text-white px-3 py-2 rounded">{reservationForm._id ? "Modifier" : "Ajouter"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- Business Expenses Table --- */}
      <div className="bg-white p-4 rounded shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Business Expenses</h2>
          <button onClick={() => { setBusinessExpenseForm({ name: "", value: "", date: "", comment: "", is_payed: false, provider: "", category: "", _id: null }); setShowBusinessExpenseModal(true); }} className="bg-green-500 text-white px-3 py-1 rounded">+ Ajouter Dépense</button>
        </div>
        <table className="w-full table-auto border-collapse border">
          <thead>
            <tr className="bg-gray-200">
              <th className="border px-2 py-1">Name</th>
              <th className="border px-2 py-1">Value</th>
              <th className="border px-2 py-1">Date</th>
              <th className="border px-2 py-1">Provider</th>
              <th className="border px-2 py-1">Category</th>
              <th className="border px-2 py-1">Payed?</th>
              <th className="border px-2 py-1">Comment</th>
              <th className="border px-2 py-1">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBusinessExpenses.map(bexp => (
              <tr key={bexp._id}>
                <td className="border px-2 py-1">{bexp.name}</td>
                <td className="border px-2 py-1">{bexp.value}</td>
                <td className="border px-2 py-1">{new Date(bexp.date).toLocaleDateString()}</td>
                <td className="border px-2 py-1">{bexp.provider}</td>
                <td className="border px-2 py-1">{bexp.category}</td>
                <td className="border px-2 py-1">{bexp.is_payed ? "Yes" : "No"}</td>
                <td className="border px-2 py-1">{bexp.comment}</td>
                <td className="border px-2 py-1 space-x-1">
                  <button onClick={() => { setBusinessExpenseForm(bexp); setShowBusinessExpenseModal(true); }} className="bg-yellow-400 px-2 py-1 rounded">Edit</button>
                  <button onClick={() => deleteBusinessExpense(bexp._id)} className="bg-red-500 text-white px-2 py-1 rounded">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Business Expense Modal */}
      {showBusinessExpenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-96 max-h-96 overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{businessExpenseForm._id ? "Modifier Dépense" : "Ajouter Dépense"}</h2>
            <form onSubmit={(e) => { saveBusinessExpense(e); setShowBusinessExpenseModal(false); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium">Name</label>
                <input type="text" className="w-full border p-2 rounded" value={businessExpenseForm.name} onChange={e => setBusinessExpenseForm({ ...businessExpenseForm, name: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium">Value</label>
                <input type="number" className="w-full border p-2 rounded" value={businessExpenseForm.value} onChange={e => setBusinessExpenseForm({ ...businessExpenseForm, value: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium">Date</label>
                <input type="date" className="w-full border p-2 rounded" value={formatDateInput(businessExpenseForm.date)} onChange={e => setBusinessExpenseForm({ ...businessExpenseForm, date: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium">Provider</label>
                <input type="text" className="w-full border p-2 rounded" value={businessExpenseForm.provider} onChange={e => setBusinessExpenseForm({ ...businessExpenseForm, provider: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium">Category</label>
                <input type="text" className="w-full border p-2 rounded" value={businessExpenseForm.category} onChange={e => setBusinessExpenseForm({ ...businessExpenseForm, category: e.target.value })} />
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={businessExpenseForm.is_payed} onChange={e => setBusinessExpenseForm({ ...businessExpenseForm, is_payed: e.target.checked })} />
                  <span className="text-sm font-medium">Is Payed</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium">Comment</label>
                <input type="text" className="w-full border p-2 rounded" value={businessExpenseForm.comment} onChange={e => setBusinessExpenseForm({ ...businessExpenseForm, comment: e.target.value })} />
              </div>
              <div className="flex gap-2 pt-4">
                <button type="button" onClick={() => setShowBusinessExpenseModal(false)} className="flex-1 bg-gray-400 text-white px-3 py-2 rounded">Annuler</button>
                <button type="submit" className="flex-1 bg-blue-500 text-white px-3 py-2 rounded">{businessExpenseForm._id ? "Modifier" : "Ajouter"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default Resa;
